<template>
  <div class="b-leaseitnow-calculator" :class="{ 'is-sticky': !autoSubmit }">
    <Loader v-if="isLoading" display="absolute"></Loader>
    <Alert v-if="errorMsgs.length" type="danger" :messages="errorMsgs"></Alert>

    <div class="b-leaseitnow-calculator__header">
      <h2 class="h2" :class="{ 'mb-5': !autoSubmit }">
        {{ dictionary.calculatorHeaderTitle }}
      </h2>
      <h3 v-if="!autoSubmit" class="h3">
        {{ dictionary.calculatorHeaderSubTitle }}
      </h3>
    </div>

    <div class="b-leaseitnow-calculator__body">
      <form autocomplete="off" @submit.prevent="onSubmit">
        <div>
          <label for="type" class="form-label semibold mb-12">
            {{ dictionary.typeLabel }}
          </label>
        </div>

        <div class="mb-16">
          <div class="form-check form-switch form-switch-theme-yellow">
            <label class="form-check-label" for="type">
              <span :class="{ bold: !type }">
                {{ dictionary.privateTypeLabel }}
              </span>
            </label>
            <input
              id="type"
              v-model="type"
              class="form-check-input"
              type="checkbox"
              role="switch"
              @change="onChangeType" />
            <label for="type" class="semibold cursor-pointer">
              <span :class="{ bold: type }">
                {{ dictionary.commercialTypeLabel }}
              </span>
            </label>
          </div>
        </div>

        <hr class="mb-16" />

        <div class="form-label semibold mb-12">
          {{ dictionary.filtersLabel }}
        </div>

        <div class="mb-16">
          <FilterBrand
            ref="filterBrand"
            :options="brandsOptions"
            @change-selected="onChangeBrand" />
        </div>

        <div class="mb-16">
          <FilterModel
            ref="filterModel"
            :options="modelsOptions"
            :disabled="isDisabledFilterModel"
            @change-selected="onChangeModel" />
        </div>

        <div class="mb-16">
          <FilterEdition
            ref="filterEdition"
            :options="editionsOptions"
            :disabled="isDisabledFilterEdition"
            @change-selected="onChangeEdition" />
        </div>

        <div class="mb-16">
          <FilterFuel
            ref="filterFuel"
            :options="fuelsOptions"
            @change-selected="onChangeFuel" />
        </div>

        <div class="mb-24">
          <FilterTransmission
            ref="filterTransmission"
            :options="transmissionsOptions"
            @change-selected="onChangeTransmission" />
        </div>

        <div class="mb-16">
          <div class="form-label semibold mb-0">
            {{ dictionary.pricePlaceholder }}
          </div>
        </div>

        <div class="mb-32 clear-last-child">
          <FilterPrice
            v-if="pricesDefaults.length"
            ref="filterPrice"
            :min-value="pricesDefaults[0]"
            :max-value="pricesDefaults[1]"
            :disabled="isDisabledFilterPrice"
            @change-selected="onChangePrice" />
        </div>

        <div v-if="!autoSubmit" class="mb-24">
          <button type="submit" class="btn btn-primary btn-full-width">
            {{ dictionary.buttonCalculatorSubmitText }}
          </button>
        </div>

        <div
          v-if="showClearFilters"
          class="b-leaseitnow-calculator__trash mb-24 clear-last-child">
          <button
            type="button"
            class="btn btn-clear btn-icon-start"
            @click="onClickClearFilters">
            <i class="icon-bin"></i>
            {{ dictionary.buttonCalculatorClearAllText }}
          </button>
        </div>

        <div v-if="!autoSubmit" class="b-leaseitnow-calculator__disclaimer">
          <span>
            {{ dictionary.disclaimerText }}
          </span>
        </div>
      </form>
    </div>
  </div>

  <ModalValidations :errors="validationErrors" />
</template>

<script>
import { Modal } from 'bootstrap'
import useVuelidate from '@vuelidate/core'
import { minValue } from '@vuelidate/validators'
import axios from 'axios'
import Alert from '../components/common/Alert.vue'
import Loader from '../components/common/Loader.vue'
import { v4 as uuidv4 } from 'uuid'
import FilterBrand from './../components/leaseitnow/FilterBrand.vue'
import FilterModel from './../components/leaseitnow/FilterModel.vue'
import FilterEdition from './../components/leaseitnow/FilterEdition.vue'
import FilterFuel from './../components/leaseitnow/FilterFuel.vue'
import FilterTransmission from './../components/leaseitnow/FilterTransmission.vue'
import FilterPrice from './../components/leaseitnow/FilterPrice.vue'
import ModalValidations from './../components/ModalValidations.vue'
import constants from './../../lib/constants'
import mixinsLeaseItNow from './../mixins/leaseitnow-mixins'
import * as lib from './../../lib/lib'

const { filters } = constants.leaseitnow

export default {
  components: {
    Alert,
    Loader,
    FilterBrand,
    FilterModel,
    FilterEdition,
    FilterFuel,
    FilterTransmission,
    FilterPrice,
    ModalValidations,
  },

  mixins: [mixinsLeaseItNow],
  props: {
    autoSubmit: {
      required: false,
      type: Boolean,
    },
  },
  emits: ['change-filters'],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      key: uuidv4(),
      isLoading: false,
      errorMsgs: [],
      modalValidations: null,
      brandsOptions: [],
      modelsOptions: [],
      editionsOptions: [],
      fuelsOptions: [],
      transmissionsOptions: [
        {
          text: this.dictionary.transmissionManual,
          value: filters.transmissionsOptions.manual,
        },
        {
          text: this.dictionary.transmissionAutomatic,
          value: filters.transmissionsOptions.auto,
        },
      ],
      pricesDefaults: [],
      validationErrors: [],
    }
  },
  validations() {
    return {
      numberOfFiltersSelected: {
        minValue: minValue(2),
      },
    }
  },
  computed: {
    type: {
      get: function () {
        // console.log('get', this.getField(filters.type))
        return this.getField(filters.type)
      },
      set: function (value) {
        // console.log('set', value)
        this.setField({ field: filters.type, value })
      },
    },

    numberOfFiltersSelected() {
      let count = 0

      if (this.brands.length) {
        count += 1
      }
      if (this.models.length) {
        count += 1
      }
      if (this.fuels.length) {
        count += 1
      }
      if (this.transmissions.length) {
        count += 1
      }
      return count
    },

    showClearFilters() {
      const isDefaultPriceRange =
        this.prices[0] === this.pricesDefaults[0] &&
        this.prices[1] === this.pricesDefaults[1]

      return (
        this.brands.length ||
        this.models.length ||
        this.editions.length ||
        this.fuels.length ||
        this.transmissions.length ||
        !isDefaultPriceRange
      )
    },

    isDisabledFilterModel() {
      return !this.brands.length
    },

    isDisabledFilterEdition() {
      return !this.models.length
    },

    isDisabledFilterPrice() {
      return !this.brands.length
    },
  },
  mounted() {
    this.getDefaults()
    this.setPreselectedValues()
    this.setPreselectedValuesForTransmissions()

    this.modalValidations = new Modal(
      document.getElementById('modalValidations')
    )
  },
  methods: {
    setPreselectedValues() {
      if (!this.autoSubmit) {
        return false
      }

      const searchargs = lib.getUrlParam('searchargs', null)
      if (!searchargs) {
        return false
      }

      const {
        type,
        manufacturers,
        models,
        editions,
        fuelType,
        bodyType,
        transmission,
        priceMin,
        priceMax,
      } = JSON.parse(decodeURI(searchargs))

      // console.log('setPreselectedValues', type, this.decodeTypeToken(type))
      // prettier-ignore
      this.setField({ field: `${filters.type}`, value: this.decodeTypeToken(type) })
      this.setField({ field: `${filters.brands}Args`, value: manufacturers })
      this.setField({ field: `${filters.models}Args`, value: models })
      this.setField({ field: `${filters.editions}Args`, value: editions })
      this.setField({ field: `${filters.fuels}Args`, value: fuelType })
      this.setField({ field: `${filters.bodyTypes}`, value: bodyType })
      // prettier-ignore
      this.setField({ field: `${filters.transmissions}`, value: transmission ? [transmission] : [] })
      this.setField({ field: `${filters.prices}`, value: [priceMin, priceMax] })

      this.$emit('change-filters')
    },

    setPreselectedValuesForBrands() {
      if (!this.autoSubmit) {
        return false
      }

      const searchargs = lib.getUrlParam('searchargs', null)
      if (!searchargs) {
        return false
      }

      const brands = JSON.parse(decodeURI(searchargs)).manufacturers

      if (brands.length) {
        this.setField({
          field: filters.brands,
          value: brands.map((x) => x.id),
        })

        this.getModels(true)
      }
    },

    setPreselectedValuesForModels() {
      if (!this.autoSubmit) {
        return false
      }

      const searchargs = lib.getUrlParam('searchargs', null)
      if (!searchargs) {
        return false
      }

      const models = JSON.parse(decodeURI(searchargs)).models

      if (models.length) {
        this.setField({
          field: filters.models,
          value: models.map((x) => x.modelKey),
        })
        this.getEditions()
      }
    },

    setPreselectedValuesForEditions() {
      if (!this.autoSubmit) {
        return false
      }

      const searchargs = lib.getUrlParam('searchargs', null)
      if (!searchargs) {
        return false
      }

      const editions = JSON.parse(decodeURI(searchargs)).editions

      if (editions.length) {
        this.setField({
          field: filters.editions,
          value: editions.map((x) => x.edition),
        })
      }
    },

    setPreselectedValuesForFuels() {
      if (!this.autoSubmit) {
        return false
      }

      const searchargs = lib.getUrlParam('searchargs', null)
      if (!searchargs) {
        return false
      }
      const fuelType = JSON.parse(decodeURI(searchargs)).fuelType

      if (fuelType.length) {
        this.setField({
          field: filters.fuels,
          value: fuelType.map((x) => x.name),
        })
      }
    },

    setPreselectedValuesForTransmissions() {
      if (!this.autoSubmit) {
        return false
      }

      const searchargs = lib.getUrlParam('searchargs', null)
      if (!searchargs) {
        return false
      }
      const value = JSON.parse(decodeURI(searchargs)).transmission
      if (value) {
        this.setField({ field: filters.transmissions, value })
      }
    },

    setPreselectedValuesForPrices() {
      if (!this.autoSubmit) {
        return false
      }

      const searchargs = lib.getUrlParam('searchargs', null)
      if (!searchargs) {
        return false
      }

      const { priceMin, priceMax } = JSON.parse(decodeURI(searchargs))

      this.setField({ field: filters.prices, value: [priceMin, priceMax] })
    },

    getDefaults() {
      this.getBrands()
      this.getFuels()
      this.getRentalRange()
    },

    setSearchArgs() {
      const searchargs = JSON.stringify(this.getCarsPayload())
      const { sessionStorageKey } = constants.leaseitnow
      sessionStorage.setItem(sessionStorageKey, searchargs)

      if (this.autoSubmit) {
        const { language } = this.$route.params

        this.$router.push({
          name: 'home',
          params: { language },
          query: { searchargs: searchargs },
        })
      }
    },

    onChangeType() {
      this.clearFilters()
      this.getDefaults()
      this.setSearchArgs()
    },

    onChangeBrand() {
      this.filterExistingModels()
      this.filterExistingEditions()

      this.getModels()

      if (this.models.length) {
        this.getEditions()
      }
      this.setSearchArgs()
      this.$emit('change-filters')
    },

    onChangeModel() {
      this.filterExistingEditions()

      this.getEditions()
      this.setSearchArgs()
      this.$emit('change-filters')
    },

    onChangeEdition() {
      this.setSearchArgs()
      this.$emit('change-filters')
    },

    onChangeFuel() {
      this.setSearchArgs()
      this.$emit('change-filters')
    },

    onChangeTransmission() {
      this.setSearchArgs()
      this.$emit('change-filters')
    },

    onChangePrice() {
      this.setSearchArgs()
      this.$emit('change-filters')
    },

    onClickClearFilters() {
      this.clearFilters()
      this.setSearchArgs()
      this.$emit('change-filters')
    },

    clearFilters() {
      this.setField({ field: filters.brands, value: [] })
      this.setField({ field: `${filters.brands}Args`, value: [] })
      this.setField({ field: filters.models, value: [] })
      this.setField({ field: `${filters.models}Args`, value: [] })
      this.setField({ field: filters.editions, value: [] })
      this.setField({ field: `${filters.editions}Args`, value: [] })
      this.setField({ field: filters.fuels, value: [] })
      this.setField({ field: `${filters.fuels}Args`, value: [] })
      this.setField({ field: filters.transmissions, value: [] })
      this.setField({ field: filters.prices, value: this.pricesDefaults })
    },

    clearEditions() {
      this.setField({ field: filters.editions, value: [] })
      this.setField({ field: `${filters.editions}Args`, value: [] })
    },

    onSubmit() {
      this.v$.$validate()

      if (this.v$.$invalid) {
        this.setValidationErrors()
        this.modalValidations.show()
        return false
      }

      this.isLoading = true
      const value = this.getCarsPayload()
      const url = `${
        this.configuration.applicationUrl
      }?searchargs=${encodeURIComponent(JSON.stringify(value))}`
      window.location.assign(url)
    },

    getBrands() {
      this.isLoading = true

      // prettier-ignore
      const url = `${this.configuration.endpointGetManufacters}/${this.getTypeToken()}`

      axios
        .get(url)
        .then((response) => {
          const { hasError, errorMessage } = response.data

          if (hasError) {
            this.handleError([errorMessage])
            return false
          }
          this.brandsOptions = response.data.map((x) => ({
            value: x.id,
            text: x.name,
            apiObject: x,
          }))
          this.setPreselectedValuesForBrands()
          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    getModels(setPreselected) {
      this.isLoading = true

      const postData = {
        manufacturers: this.brandsArgs,
        type: this.getTypeToken(),
      }

      axios
        .post(this.configuration.endpointGetModels, postData)
        .then((response) => {
          const { hasError, errorMessage } = response.data

          if (hasError) {
            this.handleError([errorMessage])
            return false
          }

          this.modelsOptions = response.data.map((x) => ({
            value: x.modelKey,
            text: x.name,
            apiObject: x,
          }))

          if (setPreselected) {
            this.setPreselectedValuesForModels()
          }
          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    getEditions() {
      this.isLoading = true

      const postData = {
        manufacturers: this.brandsArgs,
        models: this.modelsArgs,
        type: this.getTypeToken(),
      }
      axios
        .post(this.configuration.endpointGetEditions, postData)
        .then((response) => {
          const { hasError, errorMessage } = response.data

          if (hasError) {
            this.handleError([errorMessage])
            return false
          }

          this.editionsOptions = response.data.map((x) => ({
            value: x.edition,
            text: x.label,
            apiObject: x,
          }))

          this.setPreselectedValuesForEditions()
          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    getFuels() {
      this.isLoading = true

      // prettier-ignore
      const url = `${this.configuration.endpointGetFuels}?type=${this.getTypeToken()}`

      axios
        .get(url)
        .then((response) => {
          const { hasError, errorMessage } = response.data

          if (hasError) {
            this.handleError([errorMessage])
            return false
          }

          this.fuelsOptions = response.data.map((x) => ({
            value: x.name,
            text: x.label,
            apiObject: x,
          }))
          this.setPreselectedValuesForFuels()
          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    getRentalRange() {
      this.isLoading = true

      axios
        .get(this.configuration.endpointGetRentalRange)
        .then((response) => {
          const { hasError, errorMessage } = response.data

          if (hasError) {
            this.handleError([errorMessage])
            return false
          }

          this.pricesDefaults = [response.data.min, response.data.max]
          this.setField({ field: filters.prices, value: this.pricesDefaults })
          this.setPreselectedValuesForPrices()
          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    filterExistingModels() {
      const array = []
      const arrayArgs = []

      this.models.forEach((element) => {
        const entry = this.modelsOptions.find((x) => x.value === element)
        const manufacturerId = entry.apiObject.manufacturer_id.toString()

        if (this.brands.includes(manufacturerId)) {
          array.push(element)
          arrayArgs.push(entry.apiObject)
        }
      })

      this.setField({ field: filters.models, value: array })
      this.setField({ field: `${filters.models}Args`, value: arrayArgs })
    },

    filterExistingEditions() {
      const array = []
      const arrayArgs = []

      this.editions.forEach((element) => {
        const entry = this.editionsOptions.find((x) => x.value === element)
        const modelKey = entry.apiObject.model_key

        if (this.models.includes(modelKey)) {
          array.push(element)
          arrayArgs.push(entry.apiObject)
        }

        // const manufacturerId = entry.apiObject.manufacturer_id.toString()
        // if (this.brands.includes(manufacturerId)) {
        //   array.push(element)
        //   arrayArgs.push(entry.apiObject)
        // }
      })

      this.setField({ field: filters.editions, value: array })
      this.setField({ field: `${filters.editions}Args`, value: arrayArgs })
    },

    setValidationErrors() {
      const array = []
      const { filtersMinValue } = this.dictionary

      if (this.v$.numberOfFiltersSelected.minValue.$invalid) {
        array.push(filtersMinValue)
      }

      this.validationErrors = array
    },

    handleSuccess() {
      this.isLoading = false
      this.errorMsgs = []
    },

    handleError(messages) {
      this.errorMsgs = messages
      this.isLoading = false
    },
  },
}
</script>
